"use client";

import { Quotes } from "@phosphor-icons/react/dist/ssr";
import { z } from "zod";

import {
  Carousel,
  CarouselContent,
  CarouselDotButton,
  CarouselDotNavigation,
  CarouselItem,
} from "@/common/components/carousel";
import { SanitizedContent } from "@/common/components-2/sanitized-content";
import { Section } from "@/common/components-2/section";
import { CmsSectionHeading } from "@/common/components-2/section-heading/cms-section-heading";
import type { HranoPage } from "@/payload-types";
import { EmitErrorReport } from "@/services/cms/components/emit-error-report";

const testimonialsSectionSchema = z.object({
  heading: z.string(),
  items: z.array(
    z.object({
      name: z.string(),
      description: z.string(),
      text: z.string(),
    }),
  ),
});

export function TestimonialsSection(props: HranoPage["ts"]) {
  let propsData: z.infer<typeof testimonialsSectionSchema>;

  try {
    propsData = testimonialsSectionSchema.parse(props);
  } catch (ex) {
    return <EmitErrorReport error={JSON.stringify(ex)} name="Testimonials section" />;
  }

  const { heading, items } = propsData;

  return (
    <Section classes={{ section: "bg-foreground py-10 text-primary-foreground md:py-14 lg:py-20" }}>
      <CmsSectionHeading heading={heading} />
      <div className="md:mx-auto md:mt-8">
        <Carousel opts={{ align: "end" }}>
          <CarouselContent className="md:-ml-8 lg:-ml-16">
            {items.map(({ name, description, text }) => (
              <CarouselItem
                key={name}
                className="h-auto max-w-[294px] md:max-w-[432px] md:pl-8 lg:max-w-[421px] lg:pl-16"
              >
                <div className="h-full rounded-2xl bg-white p-8 text-foreground">
                  <p className="mb-2 font-serif text-3xl">{name}</p>
                  <SanitizedContent as="div" content={description} className="mb-9 text-lg" />
                  <Quotes className="mb-9 size-14" />
                  <SanitizedContent as="div" content={text} className="text-lg" />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselDotNavigation
            items={(selectedIndex) =>
              items.map(({ name }, index) => (
                <CarouselDotButton
                  className={index === selectedIndex ? "bg-dodger-blue-700" : "bg-muted-foreground"}
                  key={name}
                  index={index}
                  active={index === selectedIndex}
                  aria-label={`${index + 1}`}
                />
              ))
            }
            className="mt-8 md:hidden"
          />
        </Carousel>
      </div>
    </Section>
  );
}
